import React from 'react';
import { Alert, Card, Space, Table, Tag } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { epcEstimationService } from '@/services/epcEstimation.service';
import { EpcEstimatePerformancePanel } from './EpcEstimatePerformancePanel';
import { EpcEstimateOperatorSignoffPanel } from './EpcEstimateOperatorSignoffPanel';

export interface EpcEstimateDiagnosticsWorkbenchProps {
  estimateId: string;
  revisionId: string;
  costLineCount?: number;
  boqLineCount?: number;
}

/**
 * EST3 UX2 Wave 9 — diagnostics + performance measure + operator sign-off.
 */
export const EpcEstimateDiagnosticsWorkbench: React.FC<EpcEstimateDiagnosticsWorkbenchProps> = ({
  estimateId,
  revisionId,
  costLineCount = 0,
  boqLineCount = 0,
}) => {
  const diagQ = useQuery({
    queryKey: ['epc-est-diagnostics', estimateId],
    queryFn: () => epcEstimationService.diagnostics(estimateId),
    enabled: !!estimateId,
  });
  const findings = Array.isArray(diagQ.data?.findings)
    ? diagQ.data.findings
    : Array.isArray(diagQ.data)
      ? diagQ.data
      : [];

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Card size="small" title="Diagnostics" loading={diagQ.isLoading}>
        {diagQ.isError ? (
          <Alert type="warning" showIcon message="Could not load diagnostics (permission or session)." />
        ) : findings.length === 0 ? (
          <Alert type="success" showIcon message="No diagnostic findings." />
        ) : (
          <Table
            size="small"
            rowKey={(r: { code?: string; message?: string }) => `${r.code}-${r.message}`}
            dataSource={findings}
            pagination={{ pageSize: 20 }}
            columns={[
              {
                title: 'Severity',
                dataIndex: 'severity',
                width: 100,
                render: (s: string) => (
                  <Tag color={s === 'error' ? 'red' : s === 'warn' ? 'orange' : 'blue'}>{s}</Tag>
                ),
              },
              { title: 'Code', dataIndex: 'code', width: 160 },
              { title: 'Message', dataIndex: 'message' },
            ]}
          />
        )}
      </Card>

      <EpcEstimatePerformancePanel
        estimateId={estimateId}
        revisionId={revisionId}
        costLineCount={costLineCount}
        boqLineCount={boqLineCount}
      />

      <EpcEstimateOperatorSignoffPanel />
    </Space>
  );
};
